import React, { useEffect, useState } from 'react';
import axios from '../api/axios';
import styles from './LeaderDashboard.module.css';
import { useAuth } from '../context/AuthContext';
import { Editor } from '@tinymce/tinymce-react';
import { Users, Briefcase, FileText, Calendar, Target, AlertCircle, TrendingUp, CheckCircle, Mail } from 'lucide-react';

const LeaderDashboard: React.FC = () => {
  const { user, refreshUser } = useAuth();
  const [motd, setMotd] = useState<string>('');
  const [isEditingMotd, setIsEditingMotd] = useState<boolean>(false);
  const [motdInput, setMotdInput] = useState<string>('');
  const [nextEvent, setNextEvent] = useState<any>(null);
  const [recentPosts, setRecentPosts] = useState<any[]>([]);
  
  // Department-specific stats
  const [deptStats, setDeptStats] = useState({
    teamMembers: 0,
    deptMissions: 0,
    pendingApplications: 0,
    pendingRequests: 0,
    unreadMessages: 0
  });
  
  const [nextLeadershipEvent, setNextLeadershipEvent] = useState<any>(null);
  
  // Organization-wide stats
  const [orgStats, setOrgStats] = useState({
    totalMembers: 0,
    totalMissions: 0,
    totalEvents: 0
  });
  
  const [loading, setLoading] = useState(true);

  const tinymceApiKey =
    process.env.REACT_APP_TINYMCE_API_KEY || 'bsar1vgsn4pbkl3t3hrox0hputshymgw4y9sr7499m266n2r';

  // Get user's department from their position
  const getUserDepartment = (): string | null => {
    if (!user) return null;
    
    // Check primary position first
    if (user.primaryPosition) {
      return user.primaryPosition.toLowerCase();
    }
    
    // Fall back to secondary
    if (user.secondaryPosition) {
      return user.secondaryPosition.toLowerCase();
    }
    
    // Fall back to tertiary
    if (user.tertiaryPosition) {
      return user.tertiaryPosition.toLowerCase();
    }
    
    return null;
  };

  // Check if user is CEO or COO
  const isCeoOrCoo = (): boolean => {
    if (!user) return false;
    const titles = [
      user.primaryPositionTitle,
      user.secondaryPositionTitle,
      user.tertiaryPositionTitle
    ].filter(Boolean).map((t: string) => t.toLowerCase());
    
    return titles.includes('ceo') || titles.includes('chief executive officer') || 
           titles.includes('coo') || titles.includes('chief operating officer');
  };

  // Check if user is C-Suite (Tier 4) - has leadership access
  const isCSuite = (): boolean => {
    if (!user) return false;
    return user.positionTier === 4;
  };

  useEffect(() => {
    refreshUser();
    fetchMotd();
    fetchNextEvent();
    fetchRecentPosts();
  }, []);

  useEffect(() => {
    if (user) {
      fetchStats();
    }
  }, [user]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const userDept = user?.primaryPosition?.department?.name || '';

      // === DEPARTMENT-SPECIFIC STATS ===
      
      // Fetch YOUR team members
      let teamMembers = 0;
      if (userDept) {
        try {
          const deptRes = await axios.get(`/Departments/${userDept}/roster`);
          teamMembers = Array.isArray(deptRes.data) ? deptRes.data.length : 0;
        } catch (err) {
          console.log('Failed to fetch department roster');
        }
      }

      // Fetch active missions (TODO: Add department filtering when schema supports it)
      let deptMissions = 0;
      try {
        const missionsRes = await axios.get('/missions/all');
        const missions = Array.isArray(missionsRes.data) ? missionsRes.data : [];
        deptMissions = missions.filter((m: any) => 
          m.status === 'Open' || m.status === 'InProgress'
        ).length;
      } catch (err) {
        console.log('Failed to fetch missions', deptMissions);
      }

      // Fetch next leadership event (isLeadershipOnly = true)
      try {
        const eventsRes = await axios.get('/events', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const events = Array.isArray(eventsRes.data) ? eventsRes.data : [];
        const now = new Date();
        const upcomingLeadershipEvents = events
          .filter((event: any) => 
            event.isLeadershipOnly === true && 
            new Date(event.start) > now
          )
          .sort((a: any, b: any) => new Date(a.start).getTime() - new Date(b.start).getTime());
        
        if (upcomingLeadershipEvents.length > 0) {
          setNextLeadershipEvent(upcomingLeadershipEvents[0]);
        }
      } catch (err) {
        console.log('Failed to fetch leadership events');
      }

      // Fetch pending position applications for YOUR department
      let pendingApplications = 0;
      if (userDept) {
        try {
          const appsRes = await axios.get('/PositionApplications?status=pending');
          const apps = Array.isArray(appsRes.data) ? appsRes.data : [];
          
          // CEO/COO/Admin see all applications
          const isCeoOrCoo = [user?.primaryPositionTitle, user?.secondaryPositionTitle, user?.tertiaryPositionTitle]
            .filter(Boolean)
            .map((t: string) => t.toLowerCase())
            .some((t: string) => ['ceo', 'chief executive officer', 'coo', 'chief operating officer'].includes(t));
          
          if (user?.role === 'Admin' || isCeoOrCoo) {
            pendingApplications = apps.length;
          } else {
            // Department heads see only their department's applications
            pendingApplications = apps.filter((app: any) => 
              app.position?.department?.name === userDept
            ).length;
          }
        } catch (err) {
          console.log('No access to position applications');
        }
      }

      // Fetch pending requests count (includes permission requests + new registrations)
      let pendingRequests = 0;
      if (user?.role === 'Admin' || [4, 5, 11].includes(user?.primaryPosition?.id || 0)) {
        try {
          const countRes = await axios.get('/permissionrequests/pending-count');
          pendingRequests = countRes.data || 0;
        } catch (err) {
          console.log('No access to permission requests count');
        }
      }

      // Fetch unread leadership messages
      let unreadMessages = 0;
      if (isCSuite()) {
        try {
          const statsRes = await axios.get('/LeadershipMessages/stats');
          unreadMessages = statsRes.data.unreadCount || 0;
        } catch (err) {
          console.log('No access to leadership messages');
        }
      }

      setDeptStats({
        teamMembers,
        deptMissions,
        pendingApplications,
        pendingRequests,
        unreadMessages
      });

      // === ORGANIZATION-WIDE STATS (For Context) ===
      
      // Total org members
      let totalMembers = 0;
      try {
        const usersRes = await axios.get('/auth/user', {
          headers: { Authorization: `Bearer ${token}` },
        });
        totalMembers = Array.isArray(usersRes.data) ? usersRes.data.length : 0;
      } catch (err) {
        console.log('Failed to fetch org users');
      }

      // Total active missions across all departments
      let totalMissions = 0;
      try {
        const missionsRes = await axios.get('/missions/all');
        const missions = Array.isArray(missionsRes.data) ? missionsRes.data : [];
        totalMissions = missions.filter((m: any) => 
          m.status === 'Open' || m.status === 'InProgress'
        ).length;
      } catch (err) {
        console.log('Failed to fetch org missions', totalMissions);
      }

      // Total upcoming events across organization
      let totalEvents = 0;
      try {
        const eventsRes = await axios.get('/events', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const events = Array.isArray(eventsRes.data) ? eventsRes.data : [];
        const now = new Date();
        totalEvents = events.filter(
          (event: any) => new Date(event.start) > now
        ).length;
      } catch (err) {
        console.log('Failed to fetch org events');
      }

      setOrgStats({
        totalMembers,
        totalMissions,
        totalEvents
      });

    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMotd = async () => {
    try {
      const res = await axios.get('/Motd');
      if (res.data && res.data.length > 0) {
        setMotd(res.data[0].content);
      }
    } catch (err) {
      console.error('Failed to fetch MotD:', err);
    }
  };

  const fetchRecentPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/posts?limit=3', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecentPosts(res.data);
    } catch (err) {
      console.error('Failed to fetch recent posts:', err);
    }
  };

  const fetchNextEvent = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get('/events', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const now = new Date();
      const upcomingEvents = res.data
        .filter((event: any) => new Date(event.start) > now)
        .sort((a: any, b: any) => new Date(a.start).getTime() - new Date(b.start).getTime());

      if (upcomingEvents.length > 0) {
        setNextEvent(upcomingEvents[0]);
      }
    } catch (err) {
      console.error('Failed to fetch events:', err);
    }
  };

  const handleSaveMotd = async () => {
    try {
      const currentMotd = await axios.get('/Motd');
      if (currentMotd.data && currentMotd.data.length > 0) {
        await axios.put(`/Motd/${currentMotd.data[0].id}`, { content: motd });
      } else {
        await axios.post('/Motd', { content: motd });
      }
      alert('Message of the Day updated successfully!');
      fetchMotd();
    } catch (err) {
      console.error('Failed to update MotD:', err);
      alert('Failed to update Message of the Day.');
    }
  };

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <div className={styles.leaderDashboardContainer}>
      {/* Header Section */}
      <div className={styles.dashboardHeader}>
        <div>
          <h1 className={styles.welcomeTitle}>Welcome back, {user?.username}!</h1>
          <p className={styles.welcomeSubtitle}>
            {user?.primaryPositionTitle || 'Leader'} • {user?.primaryPosition?.department?.name || 'Leadership'} Department
          </p>
        </div>
      </div>

      {/* Your Department Stats */}
      <div className={styles.sectionContainer}>
        <h2 className={styles.sectionHeading}>🏢 Your Department</h2>
        <div className={styles.statsGrid}>
          <div className={styles.statCard}>
            <div className={styles.statIcon} style={{ backgroundColor: '#3b82f6' }}>
              <Users size={24} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{loading ? '...' : deptStats.teamMembers}</div>
              <div className={styles.statLabel}>Department Members</div>
            </div>
          </div>

          {/* Department Missions */}
          <div className={styles.statCard}>
            <div className={styles.statIcon} style={{ backgroundColor: '#10b981' }}>
              <Target size={24} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{loading ? '...' : deptStats.deptMissions}</div>
              <div className={styles.statLabel}>Active Missions</div>
            </div>
          </div>

          {/* Next Leadership Event */}
          {nextLeadershipEvent ? (
            <div className={styles.statCard} style={{ gridColumn: 'span 2', borderLeft: '4px solid #8b5cf6' }}>
              <div className={styles.leadershipEventContent}>
                <div className={styles.leadershipEventHeader}>
                  <div className={styles.statIcon} style={{ backgroundColor: '#8b5cf6' }}>
                    <Calendar size={24} />
                  </div>
                  <div>
                    <div className={styles.leadershipEventLabel}>🔒 Next Leadership Event</div>
                    <div className={styles.leadershipEventTitle}>{nextLeadershipEvent.title}</div>
                  </div>
                </div>
                <div className={styles.leadershipEventDetails}>
                  <div className={styles.leadershipEventMeta}>
                    <span>📅 {new Date(nextLeadershipEvent.start).toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric'
                    })}</span>
                    <span>🕐 {new Date(nextLeadershipEvent.start).toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      minute: '2-digit'
                    })}</span>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.statCard} style={{ gridColumn: 'span 2' }}>
              <div className={styles.statIcon} style={{ backgroundColor: '#8b5cf6' }}>
                <Calendar size={24} />
              </div>
              <div className={styles.statContent}>
                <div className={styles.emptyStateText}>No upcoming leadership events</div>
              </div>
            </div>
          )}

          {/* Pending Applications - All department leaders can see their dept's applications */}
          <div className={styles.statCard}>
            <div className={styles.statIcon} style={{ backgroundColor: '#f59e0b' }}>
              <FileText size={24} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{loading ? '...' : deptStats.pendingApplications}</div>
              <div className={styles.statLabel}>Pending Applications</div>
            </div>
          </div>

          {/* Pending Requests */}
          {(deptStats.pendingRequests > 0 || user?.role === 'Admin' || [4, 5, 11].includes(user?.primaryPosition?.id || 0)) && (
            <div className={styles.statCard}>
              <div className={styles.statIcon} style={{ backgroundColor: '#ef4444' }}>
                <AlertCircle size={24} />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>{loading ? '...' : deptStats.pendingRequests}</div>
                <div className={styles.statLabel}>Pending Requests</div>
              </div>
            </div>
          )}

          {/* Unread Leadership Messages */}
          {isCSuite() && (
            <div 
              className={styles.statCard} 
              style={{ cursor: 'pointer' }}
              onClick={() => window.location.href = '/leadership-inbox'}
            >
              <div className={styles.statIcon} style={{ backgroundColor: '#6366f1' }}>
                <Mail size={24} />
              </div>
              <div className={styles.statContent}>
                <div className={styles.statValue}>
                  {loading ? '...' : deptStats.unreadMessages}
                  {deptStats.unreadMessages > 0 && (
                    <span className={styles.badge}>{deptStats.unreadMessages}</span>
                  )}
                </div>
                <div className={styles.statLabel}>Unread Messages</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Organization-Wide Context */}
      <div className={styles.sectionContainer}>
        <h2 className={styles.sectionHeading}>🌐 Organization Overview</h2>
        <div className={styles.statsGridCompact}>
          <div className={styles.statCardCompact}>
            <div className={styles.statIcon} style={{ backgroundColor: '#6366f1' }}>
              <Users size={20} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{loading ? '...' : orgStats.totalMembers}</div>
              <div className={styles.statLabel}>Total Members</div>
            </div>
          </div>

          <div className={styles.statCardCompact}>
            <div className={styles.statIcon} style={{ backgroundColor: '#059669' }}>
              <Target size={20} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{loading ? '...' : orgStats.totalMissions}</div>
              <div className={styles.statLabel}>All Missions</div>
            </div>
          </div>

          <div className={styles.statCardCompact}>
            <div className={styles.statIcon} style={{ backgroundColor: '#7c3aed' }}>
              <Calendar size={20} />
            </div>
            <div className={styles.statContent}>
              <div className={styles.statValue}>{loading ? '...' : orgStats.totalEvents}</div>
              <div className={styles.statLabel}>All Events</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className={styles.mainContentGrid}>
        {/* Left Column - Quick Actions */}
        <div className={styles.card}>
          <h3 className={styles.sectionTitle}>⚡ Quick Actions</h3>
          <div className={styles.quickActionsList}>
            {/* Position Applications: Admin + CEO only */}
            {(user?.role === 'Admin' || 
              [user?.primaryPositionTitle, user?.secondaryPositionTitle, user?.tertiaryPositionTitle]
                .filter(Boolean)
                .map((t: string) => t.toLowerCase())
                .some((t: string) => ['ceo', 'chief executive officer'].includes(t))
            ) && (
              <a href="/position-applications" className={styles.quickActionBtn}>
                📋 Position Applications
              </a>
            )}

            {/* Manage Positions: Admin + CEO only */}
            {(user?.role === 'Admin' || 
              [user?.primaryPositionTitle, user?.secondaryPositionTitle, user?.tertiaryPositionTitle]
                .filter(Boolean)
                .map((t: string) => t.toLowerCase())
                .some((t: string) => ['ceo', 'chief executive officer'].includes(t))
            ) && (
              <a href="/positions-management" className={styles.quickActionBtn}>
                💼 Manage Positions
              </a>
            )}

            {/* Department Management: C-Suite leaders for their department, CEO/COO for all */}
            {(isCSuite() || isCeoOrCoo() || user?.role === 'Admin') && (
              <a 
                href={`/departments/${getUserDepartment() || 'command'}/manage`} 
                className={styles.quickActionBtn}
              >
                🏢 Department Management
              </a>
            )}

            {/* Mission Requests: All Leaders */}
            <a href="/leader-dashboard/missions" className={styles.quickActionBtn}>
              🎯 Mission Requests
            </a>

            {/* Org Fleet: All Leaders — see ships members have pledged for org use */}
            <a href="/leader-dashboard/org-fleet" className={styles.quickActionBtn}>
              🚀 Org Fleet
            </a>

            {/* Permission Requests: Admin + specific C-Suite positions */}
            {(user?.role === 'Admin' || [4, 5, 11].includes(user?.primaryPosition?.id || 0)) && (
              <a href="/leader-dashboard/permission-requests" className={styles.quickActionBtn}>
                🔑 Permission Requests
              </a>
            )}

            {/* User Management: Admin + CEO + COO only */}
            {(user?.role === 'Admin' || 
              [user?.primaryPositionTitle, user?.secondaryPositionTitle, user?.tertiaryPositionTitle]
                .filter(Boolean)
                .map((t: string) => t.toLowerCase())
                .some((t: string) => ['ceo', 'chief executive officer', 'coo', 'chief operating officer'].includes(t))
            ) && (
              <a href="/user-management" className={styles.quickActionBtn}>
                👥 User Management
              </a>
            )}

            {/* Posts Management: All Leaders */}
            <a href="/posts-management" className={styles.quickActionBtn}>
              📝 Posts Management
            </a>

            {/* Award Commendation: All Leaders */}
            <a href="/award-commendation" className={styles.quickActionBtn}>
              🏅 Award Commendation
            </a>

            {/* Metrics Dashboard: Admin only */}
            {user?.role === 'Admin' && (
              <a href="/metrics-dashboard" className={styles.quickActionBtn}>
                📊 Metrics Dashboard
              </a>
            )}
          </div>
        </div>

        {/* System Tools - Admin Only */}
        {user?.role === 'Admin' && (
          <div className={styles.card}>
            <h3 className={styles.sectionTitle}>🔧 System Tools</h3>
            <div className={styles.quickActionsGrid}>
              <a href="/admin/ship-data" className={styles.quickActionBtn}>
                🚀 Ship Data Manager
              </a>
            </div>
          </div>
        )}

        {/* Right Column - Updates */}
        <div className={styles.rightColumn}>
          {/* Message of the Day */}
          <div className={styles.card}>
            <h3 className={styles.sectionTitle}>📢 Message of the Day</h3>
            {motd ? (
              <div className={styles.motdContent} dangerouslySetInnerHTML={{ __html: motd }} />
            ) : (
              <p className={styles.emptyState}>No message set</p>
            )}
          </div>

          {/* Next Upcoming Event */}
          {nextEvent && (
          <div className={styles.nextEventCard}>
            <div className={styles.eventCardHeader}>
              <h3>🗓️ Next Upcoming Event</h3>
            </div>
            <div className={styles.eventContent}>
              <h4 className={styles.eventTitle}>{nextEvent.title}</h4>
              <div className={styles.eventMeta}>
                <div className={styles.eventMetaItem}>
                  <span className={styles.eventMetaLabel}>Date:</span>
                  <span className={styles.eventMetaValue}>
                    {new Date(nextEvent.start).toLocaleDateString('en-US', {
                      weekday: 'long',
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </div>
                <div className={styles.eventMetaItem}>
                  <span className={styles.eventMetaLabel}>Time:</span>
                  <span className={styles.eventMetaValue}>
                    {new Date(nextEvent.start).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
              </div>
              {nextEvent.description && (
                <div className={styles.eventDescription}>
                  <strong>Description:</strong>
                  <div dangerouslySetInnerHTML={{ __html: nextEvent.description }} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recent Posts Preview */}
        {recentPosts.length > 0 && (
          <div className={styles.recentPostsCard}>
            <div className={styles.sectionHeader}>
              <h3>📝 Recent Posts</h3>
              <a href="/posts-management" className="button small">
                View All →
              </a>
            </div>
            <div className={styles.recentPostsList}>
              {recentPosts.map((post) => (
                <div key={post.id} className={styles.recentPostItem}>
                  <h4>{post.title}</h4>
                  <span className={styles.postDate}>
                    {new Date(post.createdAt).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default LeaderDashboard;
